import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  Copy,
  MoreHorizontal,
  Search,
  Send,
  ShieldCheck,
  UserCog,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  Skeleton,
  Table,
  cn,
  useToast,
  type Column,
} from '@eombor/ui';
import { Menu } from '../../../app/layout/Menu.js';
import { CopyButton } from '../../../shared/clipboard/CopyButton.js';
import { useCopyToClipboard } from '../../../shared/clipboard/useCopyToClipboard.js';
import { t } from '../../../shared/i18n/index.js';
import { usePermission } from '../../auth/model/usePermission.js';
import type { ScopeOption } from '../../rbac/ui/ScopePicker.js';
import { staffApi, type StaffDto } from '../api/staff.api.js';
import { PasswordResetDialog } from './PasswordResetDialog.js';
import { StaffPanel } from './StaffPanel.js';
import { WorkplaceDialog } from './WorkplaceDialog.js';

const PAGE_SIZE = 20;

/**
 * Qatorni JADVAL dasturiga yopishtiriladigan matnga aylantiradi.
 *
 * Ajratgich TABULATSIYA: Excel va Google Sheets uni ustun chegarasi deb
 * o'qiydi va qator o'zi kataklarga tarqaladi. Vergul bo'lsa bo'linma
 * nomidagi vergul qatorni noto'g'ri joyda bo'lib yuborardi.
 *
 * Bo'sh maydon — bo'sh katak: `null` so'zi jadvalga tushib qolmasin.
 */
export const staffRowToText = (row: StaffDto): string =>
  [row.fullName, row.username, row.jshshir, row.orgUnitName ?? '', row.positionName ?? ''].join(
    '\t',
  );

/**
 * Qator menyusi — hozircha bitta band, lekin aynan menyu.
 *
 * «Qator ma'lumotini nusxalash» tugma bo'lib chiqsa, u qatordagi to'rtta
 * amal bilan bir qatorda turib, ular bilan bir xil vaznda ko'rinardi —
 * holbuki bu kamdan-kam kerak bo'ladigan yordamchi harakat.
 */
const RowMenu = ({ row }: { row: StaffDto }) => {
  const { copy } = useCopyToClipboard();
  const toast = useToast();

  return (
    <Menu
      label={t('staff.rowMenu')}
      triggerClassName="size-9 justify-center"
      panelClassName="w-[16rem]"
      trigger={<MoreHorizontal size={16} aria-hidden />}
    >
      <button
        type="button"
        role="menuitem"
        onClick={() => {
          void copy(staffRowToText(row)).then((ok) => {
            toast.show(ok ? t('copy.done') : t('copy.failed'), ok ? 'success' : 'danger');
          });
        }}
        className={cn(
          'flex h-10 w-full items-center gap-2 px-3 text-left text-sm text-text-muted',
          'transition-[background-color,color] duration-[var(--motion-fast)]',
          'ease-[var(--ease-standard)] hover:bg-surface-2 hover:text-text',
        )}
      >
        <Copy size={15} aria-hidden />
        {t('staff.copyRow')}
      </button>
    </Menu>
  );
};

/** Katak: qiymat + yonida nusxalash tugmasi. `group/copy` shu yerda. */
const CopyCell = ({ value, label }: { value: string; label: string }) => (
  <span className="group/copy flex items-center gap-1">
    <span className="text-text">{value}</span>
    <CopyButton value={value} label={label} />
  </span>
);

export interface StaffTableProps {
  /** Ruxsatlar oynasini ochadi. */
  onShowPermissions: (staff: StaffDto) => void;
  /**
   * Korxonalar ro'yxati — ish joyi oynasi uchun. `features/staff`
   * `features/org` ni bilmaydi, ro'yxatni ilova qatlami uzatadi.
   */
  scopeOptions: ScopeOption[];
}

/**
 * Xodimlar jadvali.
 *
 * Ro'yxat emas, JADVAL: kadrlar xodimi bir necha o'nlab qatorni yonma-yon
 * solishtiradi — kim qayerda ishlaydi, kim qaysi lavozimda. Kartochkalar
 * ro'yxatida ustunlar tekislanmaydi va solishtirish ko'z bilan qiyin bo'ladi.
 *
 * Qidiruv KECHIKTIRILGAN (300 ms) va eski natijani yangisi kelguncha ekranda
 * ushlab turadi: har harfda jadval bo'shab, sakrab tursa qidiruvni o'qib
 * bo'lmaydi.
 */
export const StaffTable = ({
  onShowPermissions,
  scopeOptions,
}: StaffTableProps) => {
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [resetting, setResetting] = useState<StaffDto | null>(null);
  const [editingWorkplace, setEditingWorkplace] = useState<StaffDto | null>(null);
  const [opened, setOpened] = useState<StaffDto | null>(null);

  const { canAnywhere } = usePermission();
  const canReset = canAnywhere('user.password.reset');
  const canEditWorkplace = canAnywhere('user.update');

  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(input.trim());
      // Yangi qidiruv — har doim birinchi sahifadan: 3-sahifada turib qidirsa,
      // natija bitta sahifaga sig'sa ham bo'sh ekran chiqardi.
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [input]);

  const staff = useQuery({
    queryKey: ['staff', 'list', query, page],
    queryFn: () => staffApi.list({ q: query, page, limit: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  const items = staff.data?.items ?? [];
  const total = staff.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const columns: Column<StaffDto>[] = [
    {
      key: 'fullName',
      header: t('staff.colName'),
      // Siljiganda ism chapda qoladi — aks holda qator kimga tegishli
      // ekani yo'qoladi va o'ngdagi tugmalar «kimningdir» tugmasi bo'lardi.
      sticky: 'left',
      render: (row) => (
        <span className="flex items-center gap-2">
          <span className="font-medium text-text">{row.fullName}</span>
          {row.isSuperadmin ? (
            <Badge tone="warn">
              <ShieldCheck size={12} aria-hidden />
              {t('staff.superadmin')}
            </Badge>
          ) : null}
        </span>
      ),
    },
    {
      key: 'username',
      header: t('staff.colUsername'),
      mono: true,
      width: '13rem',
      render: (row) => (
        <span className="group/copy flex items-center gap-1.5">
          <span className="text-text">{row.username}</span>
          {/* Telegram belgisi — parol qay yo'l bilan yetib borishini oldindan
              aytadi: ulanmagan xodimga parolni admin qo'lda beradi. */}
          {row.telegramLinked ? (
            <Send size={12} aria-hidden className="text-accent shrink-0" aria-label={t('staff.telegramLinked')} />
          ) : null}
          <CopyButton value={row.username} label={t('staff.copyUsername')} />
        </span>
      ),
    },
    {
      key: 'jshshir',
      header: t('staff.colJshshir'),
      mono: true,
      width: '13rem',
      render: (row) => <CopyCell value={row.jshshir} label={t('staff.copyJshshir')} />,
    },
    {
      key: 'orgUnitName',
      header: t('staff.colOrgUnit'),
      render: (row) =>
        row.orgUnitName === null ? (
          /* Ish joyi yo'qligi YASHIRILMAYDI: aynan shunday xodimlar rol
             berishda e'tibordan chetda qolib ketadi. */
          <span className="text-text-faint">{t('staff.noEmployment')}</span>
        ) : (
          <span className="flex flex-col gap-0.5">
            <span className="text-text">{row.orgUnitName}</span>
            {/* Bo'linma korxona OSTIDA turadi, chunki u aynan shu korxonaning
                ichki bo'limi. Alohida ustunga chiqarilsa jadval yana bir
                pog'ona kengayardi, bog'liqligi esa ko'rinmasdi. */}
            {row.subdivisionName === null ? (
              /* Bu yozuv HAR qatorda takrorlanadi va ma'lumot emas —
                 yo'qlik. Shuning uchun u ismdan ham, korxona nomidan ham
                 past ohangda: ko'z uni o'qimay o'tib ketishi kerak. */
              <span className="text-text-faint text-[0.6875rem]">{t('staff.noSubdivision')}</span>
            ) : (
              <span className="text-text-muted text-xs">{row.subdivisionName}</span>
            )}
          </span>
        ),
    },
    {
      key: 'positionName',
      header: t('staff.colPosition'),
      render: (row) => row.positionName ?? <span className="text-text-faint">—</span>,
    },
    {
      key: 'actions',
      header: t('staff.colActions'),
      align: 'right',
      width: '12rem',
      // Amallar o'ngda qoladi: ularga yetish uchun har safar jadvalni
      // oxirigacha surish kerak bo'lardi.
      sticky: 'right',
      /*
       * BITTA yo'l — panel.
       *
       * Ilgari bu yerda to'rtta havola turardi: «Ish joyi», «Rollar»,
       * «Ruxsatlar», «Parolni tiklash». Uchtasi quloqqa bir xil narsa
       * bo'lib eshitilardi va ustun jadvalning yarmini (30rem) egallardi,
       * qaysi biri asosiy ekani esa ko'rinmasdi. Hammasi panelga ko'chdi —
       * u yerda ular tartib bilan turadi va ISH JOYI bilan KIRISH HUQUQI
       * yonma-yon ko'rinadi.
       */
      render: (row) => (
        <span className="flex items-center justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={() => setOpened(row)}>
            <UserCog size={14} aria-hidden />
            {t('staff.actionOpen')}
          </Button>
          <RowMenu row={row} />
        </span>
      ),
    },
  ];

  return (
    <Card title={t('staff.listTitle')} padded={false}>
      <div className="border-b border-border px-4 py-3">
        <Input
          label={t('staff.searchLabel')}
          placeholder={t('staff.searchPlaceholder')}
          hint={t('staff.searchHint')}
          value={input}
          onChange={(event) => setInput(event.target.value)}
          prefix={<Search size={15} aria-hidden />}
          className="max-w-[24rem]"
        />
      </div>

      {staff.isPending ? (
        <div className="flex flex-col gap-2 p-4">
          {[0, 1, 2, 3, 4].map((row) => (
            <Skeleton key={row} className="h-10 w-full" />
          ))}
        </div>
      ) : staff.isError ? (
        <EmptyState
          className="m-4"
          title={t('staff.errorTitle')}
          description={t('staff.errorHint')}
          action={
            <Button variant="secondary" onClick={() => void staff.refetch()}>
              {t('staff.retry')}
            </Button>
          }
        />
      ) : (
        <Table
          caption={t('staff.listTitle')}
          columns={columns}
          rows={items}
          rowKey={(row) => row.id}
          density="compact"
          empty={
            <EmptyState
              title={query === '' ? t('staff.emptyTitle') : t('staff.notFoundTitle')}
              description={query === '' ? t('staff.emptyHint') : t('staff.notFoundHint')}
            />
          }
        />
      )}

      {lastPage > 1 ? (
        <div className="flex items-center justify-between border-t border-border px-4 py-3">
          <span className="text-xs text-text-muted">
            {t('staff.pageOf', { page: String(page), last: String(lastPage), total: String(total) })}
          </span>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((current) => current - 1)}
            >
              {t('staff.previous')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={page >= lastPage}
              onClick={() => setPage((current) => current + 1)}
            >
              {t('staff.next')}
            </Button>
          </div>
        </div>
      ) : null}

      {/*
        Panel oynalardan OLDIN chiziladi: ish joyi oynasi uning USTIDA
        ochiladi va yopilganda panel joyida qoladi — administrator
        jadvalga qaytib, xodimni qaytadan topishga majbur bo'lmaydi.
      */}
      <StaffPanel
        staff={opened}
        onClose={() => setOpened(null)}
        scopeOptions={scopeOptions}
        onEditWorkplace={canEditWorkplace ? setEditingWorkplace : null}
        onShowPermissions={onShowPermissions}
        onResetPassword={canReset ? setResetting : null}
      />

      {/* Jadval ICHIDA emas, yonida: oyna ochilishi qatorlarni qayta
          chizishga sabab bo'lmasligi kerak. */}
      <WorkplaceDialog
        staff={editingWorkplace}
        scopeOptions={scopeOptions}
        onClose={() => setEditingWorkplace(null)}
        onSaved={() => void staff.refetch()}
      />

      <PasswordResetDialog
        staff={resetting}
        onClose={() => {
          setResetting(null);
          // Telegram bog'lanishi o'zgargan bo'lishi mumkin emas, lekin
          // ro'yxatni yangilash arzon va holat har doim toza qoladi.
          void staff.refetch();
        }}
      />
    </Card>
  );
};
